import React, { useState } from 'react';
import { FileText, CheckCircle, AlertCircle, Edit2, Save, X } from 'lucide-react';

const MetaTagManager = ({ pages }) => {
  const [editingPage, setEditingPage] = useState(null);
  const [editedData, setEditedData] = useState({});

  const getStatusIcon = (status) => {
    if (status === 'optimized') return <CheckCircle className="text-green-600" size={18} />;
    if (status === 'needs-improvement') return <AlertCircle className="text-orange-600" size={18} />;
    return <AlertCircle className="text-red-600" size={18} />;
  };

  const getStatusLabel = (status) => {
    if (status === 'optimized') return 'Optimisé';
    if (status === 'needs-improvement') return 'À améliorer';
    return 'Meta manquante';
  };

  const getStatusColor = (status) => {
    if (status === 'optimized') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'needs-improvement') return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const handleEdit = (page) => {
    setEditingPage(page?.url);
    setEditedData({
      title: page?.title,
      metaDescription: page?.metaDescription
    });
  };

  const handleSave = () => {
    // In production, save to backend
    console.log('Saving meta tags:', editedData);
    setEditingPage(null);
  };

  const handleCancel = () => {
    setEditingPage(null);
    setEditedData({});
  };

  const getCharacterCount = (text, max) => {
    const count = text?.length || 0;
    const color = count > max ? 'text-red-600' : count > max * 0.9 ? 'text-orange-600' : 'text-gray-600';
    return <span className={color}>{count}/{max}</span>;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="text-green-600" size={24} />
          Gestion des Meta Tags
        </h2>
        <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          Édition en masse
        </button>
      </div>

      <div className="space-y-4">
        {pages?.map((page, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <p className="font-medium text-gray-900">{page?.url}</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border flex items-center gap-1 ${getStatusColor(page?.status)}`}>
                    {getStatusIcon(page?.status)}
                    {getStatusLabel(page?.status)}
                  </span>
                </div>
              </div>
              {editingPage !== page?.url && (
                <button
                  onClick={() => handleEdit(page)}
                  className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                >
                  <Edit2 size={18} />
                </button>
              )}
            </div>

            {editingPage === page?.url ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de la page {getCharacterCount(editedData?.title, 60)}
                  </label>
                  <input
                    type="text"
                    value={editedData?.title}
                    onChange={(e) => setEditedData({ ...editedData, title: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Titre SEO (max 60 caractères)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description {getCharacterCount(editedData?.metaDescription, 160)}
                  </label>
                  <textarea
                    value={editedData?.metaDescription}
                    onChange={(e) => setEditedData({ ...editedData, metaDescription: e?.target?.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Description SEO (max 160 caractères)"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Save size={18} />
                    Enregistrer
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
                  >
                    <X size={18} />
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Titre</p>
                  <p className="text-sm text-gray-900 font-medium">{page?.title || <span className="text-red-600">Non défini</span>}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Meta Description</p>
                  <p className="text-sm text-gray-900">{page?.metaDescription || <span className="text-red-600">Non définie</span>}</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetaTagManager;